import FormFooter from "@/components/form-footer";
import { LAYOUT_OPTIONS } from "@/config/enums";
import { useLayout } from "@/layouts/use-layout";
import routes from "@/routers/routes";
import { updatePartner, createPartner } from "@/server/api-services";
import { Partner } from "@/types";
import { zodResolver } from "@hookform/resolvers/zod";
import { useState, useEffect } from "react";
import { useForm, SubmitHandler, FormProvider } from "react-hook-form";
import toast from "react-hot-toast";
import { useNavigate } from "react-router-dom";
import { cn } from "rizzui";
import FormNav, { FormParts } from "./form-nav";
import { defaultValues } from "./form-utils";
import FormPartner from "./form-partner";
import { PartnerFormSchema } from "@/validators/create-partner.schema";

const MAP_STEP_TO_COMPONENT = {
    [FormParts.PartnerInfo]: FormPartner, // Update to FormPartner
};

interface IndexProps {
    id?: string;
    className?: string;
    partner?: Partner; // Update to Partner
    isEdit?: boolean;
}

export default function CreateEditPartner({
    id,
    partner,
    className,
    isEdit,
}: IndexProps) {
    const navigate = useNavigate();
    const { layout } = useLayout();
    const [isLoading, setLoading] = useState(false);

    const methods = useForm<Partner>({
        defaultValues: partner ? partner : defaultValues(partner),
        resolver: zodResolver(PartnerFormSchema),
    });

    useEffect(() => {
        if (isEdit && partner) {
            methods.reset(
                defaultValues({
                    ...partner,
                }),
            );
        }
    }, [isEdit, partner]);

    const onSubmit: SubmitHandler<Partner> = async (data) => {
        setLoading(true);
        try {
            if (isEdit) {
                await updatePartner(data.id ?? "", data);
            } else {
                await createPartner(data);
            }
            navigate(routes.settings.partners.list);
        } catch (error) {
            toast.error("Failed to create partner");
            console.error("Failed to create partner", error);
        } finally {
            setLoading(false);
        }
    };

    console.log("errors", methods.formState?.errors);

    return (
        <div className="@container">
            <FormNav
                className={cn(
                    layout === LAYOUT_OPTIONS.BERYLLIUM && "2xl:top-[72px]",
                )}
            />
            <FormProvider {...methods}>
                <form
                    className={cn("mt-6", className)}
                    onSubmit={methods.handleSubmit(onSubmit)}
                >
                    <div className="mb-10 grid gap-7 divide-y divide-dashed divide-gray-200 @2xl:gap-9 @3xl:gap-11">
                        {Object.entries(MAP_STEP_TO_COMPONENT).map(
                            ([key, Component]) => (
                                <div key={key}>
                                    <Component className="pt-7 @2xl:pt-9 @3xl:pt-11" />
                                </div>
                            ),
                        )}
                    </div>
                    <FormFooter
                        isLoading={isLoading}
                        handleAltBtn={() => navigate(-1)}
                        altBtnText="Cancel"
                        submitBtnText={id ? "Update Partner" : "Create Partner"} // Update button text
                    />                    
                </form>
            </FormProvider>
        </div>
    );
}
